import React, { useEffect, useMemo } from 'react'
import { FieldErrors } from 'react-hook-form'
import get from 'lodash/get'
import { ControlledHiddenInput, ControlledTextArea } from 'transfix-form/components'
import { makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui'
import { TEditAs2CertificateModalSchema } from '../schema/EditAs2CertificateModalSchema'
import { useEditAs2CertificateModalFormContext } from './useEditAs2CertificateModalForm'

interface IEditAs2CertificateModalFormProps {
  validateHandler: (body: string) => void
  error?: string | null
}

const getErrorMessage = <T extends FieldErrors<TEditAs2CertificateModalSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

const useEditAs2CertificateModalFormStyles = makeStyles(theme => ({
  button: {
    marginTop: theme.spacing(2),
  },
  errorText: {
    color: brandColors.error,
  },
  successText: {
    color: brandColors.green2,
  },
}))

export const EditAs2CertificateModalForm = ({
  error,
  validateHandler,
}: IEditAs2CertificateModalFormProps) => {
  const classes = useEditAs2CertificateModalFormStyles()

  const { control, errors, watch } = useEditAs2CertificateModalFormContext()
  const { body } = watch(['body'])

  /**
   * Checks when the body changes and validates input value
   */
  useEffect(() => {
    validateHandler(body)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body])

  const formBodyErrors = useMemo(() => getErrorMessage(errors, 'body'), [errors])

  return (
    <form data-test='EditAs2CertificateModalForm'>
      <ControlledHiddenInput name='id' control={control} />
      <ControlledTextArea
        name='body'
        control={control}
        TextAreaProps={{
          label: 'Certificate Body',
          required: true,
          invalid: !!error || !!formBodyErrors,
          placeholder: 'Enter the certificate body',
          dataTest: 'input-certificiate-body',
        }}
      />
      {(!!formBodyErrors || error) && (
        <p className={classes.errorText}>{formBodyErrors || error}</p>
      )}
    </form>
  )
}
